import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConvertFailure, ConvertResponse } from './convert.worker';

/** Driving the document converter from React. One worker per component, created
 *  on the first conversion rather than on mount: most samples are not documents
 *  and the module is expensive. */

export type ConversionState =
  | { status: 'idle' }
  | { status: 'converting' }
  | { status: 'done'; markdown: string }
  | { status: 'failed'; reason: ConvertFailure };

/** What each failure means in the analyst's terms. */
export function failureMessage(reason: ConvertFailure): string {
  switch (reason) {
    case 'unsupported':
      return 'This format cannot be converted to readable text.';
    case 'encrypted':
      return 'The document is encrypted. Its content cannot be read without the password.';
    case 'malformed':
      return 'The document is structurally damaged; no content could be extracted.';
    case 'resourceLimit':
      return 'The document crossed a safety limit while being read, so conversion stopped.';
    case 'missingPart':
      return 'The document is missing a part it needs to render.';
    case 'unavailable':
      return 'The preview converter could not start.';
  }
}

export function useConverter() {
  const workerRef = useRef<Worker | null>(null);
  const nextId = useRef(0);
  /** The request still wanted, so a slow conversion cannot overwrite a newer
   *  preview. */
  const wantedId = useRef(-1);
  const [state, setState] = useState<ConversionState>({ status: 'idle' });

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const convert = useCallback((bytes: Uint8Array) => {
    if (!workerRef.current) {
      workerRef.current = new Worker(new URL('./convert.worker.ts', import.meta.url), {
        type: 'module',
      });
      workerRef.current.onmessage = (event: MessageEvent<ConvertResponse>) => {
        const result = event.data;
        if (result.id !== wantedId.current) return;
        setState(
          result.ok
            ? { status: 'done', markdown: result.markdown }
            : { status: 'failed', reason: result.reason },
        );
      };
      workerRef.current.onerror = () => {
        setState({ status: 'failed', reason: 'unavailable' });
      };
    }

    const id = nextId.current++;
    wantedId.current = id;
    setState({ status: 'converting' });
    // Transferred rather than copied: it is the whole sample.
    workerRef.current.postMessage({ id, bytes }, [bytes.buffer]);
  }, []);


  return { state, convert };
}
